import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '../../../../../../../../components/Common/UI/Dialog';
import { cn } from '../../../../../../../../utils/styles';
import Icons from '../../../../../../../../components/Common/Icons/Icons';
import Separator from '../../../../../../../../components/Common/UI/Separator';
import { Button } from '../../../../../../../../components/Common/UI/Button';
import { Input } from '../../../../../../../../components/Common/UI/Input';

export type InvalidFields = Partial<Record<keyof IForm, boolean>>;

interface IForm {
  email: string;
  password: string;
  confirmPassword: string;
}

const initValues: IForm = {
  email: 'carson@guidancefinancial.com',
  password: 'password',
  confirmPassword: 'password',
};

const initValidateState: InvalidFields = {
  email: false,
  password: false,
  confirmPassword: false,
};

function ModalSendStatement() {
  const [modalOpen, setModalOpen] = useState(false);

  const [state, setState] = useState<IForm>();
  const [invalidFields, setInvalidFields] = useState<InvalidFields>(initValidateState);

  const handleInputChange = (key: keyof IForm, value: string) => {
    if (!state) return;

    setState({ ...state, [key]: value });
    handleValidate(key, value);
  };

  const handleValidate = (key: keyof IForm, value: string) => {
    setInvalidFields(prevState => ({ ...prevState, [key]: !value.length }));
  };

  const handleSubmit = () => {
    if (!state) return;
    const newInvalidFields: InvalidFields = { ...invalidFields };

    Object.entries(state).forEach(([key, value]) => {
      if (key in invalidFields) {
        newInvalidFields[key as keyof IForm] = !value.length;
      }
    });

    setInvalidFields(newInvalidFields);

    if (!Object.values(newInvalidFields).some(el => el)) {
      setModalOpen(false);
    }
  };

  useEffect(() => {
    setState(initValues);
    setInvalidFields(initValidateState);
  }, [modalOpen]);

  return (
    <Dialog open={modalOpen} onOpenChange={isOpen => setModalOpen(isOpen)}>
      <DialogTrigger variant="ghost" className="flex gap-[10px] p-0 justify-start h-auto">
        <Icons.SendNew className="[&_path]:fill-primary" />
        <div className="text-primary text-[16px] font-medium">Send Statement</div>
      </DialogTrigger>
      <DialogContent className={cn('max-h-[98%] max-w-[690px]')}>
        <DialogTitle className="mb-[10px] font-normal text-[16px] flex gap-[20px] items-start">
          <div className="bg-[#307BB2] p-[5px] rounded-lg w-[35px] h-[35px] flex items-center justify-center">
            <Icons.UserAccountNew className="[&_path]:fill-white w-[20px] h-[20px]" />
          </div>
          <div>
            <div className="font-bold text-xl leading-[24px]">Send Statement</div>
            <div className="opacity-60">Encrypted Statement Credentials</div>
          </div>
        </DialogTitle>
        <div className="grid gap-[20px]">
          <input type="text" className="h-0 w-0" />
          <Input
            placeholder="Enter Email"
            label="Email Address"
            inputClassName="select-none"
            error={invalidFields.email ? 'This field is required' : null}
            onChange={e => handleInputChange('email', e.target.value)}
            onBlur={e => handleValidate('email', e.target.value)}
            value={state?.email || ''}
            maxLength={75}
          />
          <Input
            placeholder="Enter Password"
            label="Password"
            type="password"
            error={invalidFields.password ? 'This field is required' : null}
            onChange={e => handleInputChange('password', e.target.value)}
            onBlur={e => handleValidate('password', e.target.value)}
            value={state?.password}
            maxLength={75}
          />
          <Input
            placeholder="Confirm Password"
            label="Confirm Password"
            type="password"
            error={invalidFields.confirmPassword ? 'This field is required' : null}
            onChange={e => handleInputChange('confirmPassword', e.target.value)}
            onBlur={e => handleValidate('confirmPassword', e.target.value)}
            value={state?.confirmPassword}
            maxLength={75}
          />
        </div>
        <Separator className="m-0" />

        <div className="flex justify-end gap-4">
          <Button variant="link" className="py-3 hover:no-underline" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="default"
            className="py-3 !bg-addition5 hover:opacity-85 flex items-center gap-[5px] px-[20px]"
            onClick={handleSubmit}
          >
            <Icons.SendNew className="[&_path]:fill-white" />
            Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ModalSendStatement;
